import { EquityErrorType } from "@/generated/TradingGateway"
import { assertNever } from "@/utils"

import type { EquitiesServiceName } from "./errorAlert"

/**
 * Services whose gateway returns `EquityError` and therefore own a
 * `<source>.errors.<errorType>` block in every locale file. Adding a source
 * here without adding its locale entries fails `equityErrorKey.test.ts`.
 */
export const EQUITY_ERROR_SOURCES = [
  "instruments",
  "marketData",
] as const satisfies readonly EquitiesServiceName[]

export type EquityErrorSource = (typeof EQUITY_ERROR_SOURCES)[number]

/**
 * assertNever makes this a compile error if codegen adds a variant, so a new
 * server error can never reach the UI as a raw enum name.
 */
const errorTypeSegment = (error: EquityErrorType): string => {
  switch (error) {
    case EquityErrorType.ClientError:
      return "clientError"
    case EquityErrorType.AlpacaError:
      return "alpacaError"
    case EquityErrorType.InternalError:
      return "internalError"
    case EquityErrorType.ServiceUnavailable:
      return "serviceUnavailable"
    default:
      return assertNever(error)
  }
}

/**
 * Builds the i18n key for a server error type, e.g. `instruments.errors.alpacaError`.
 * Keys are assembled rather than written out, so grep for `<source>.errors` in
 * the locale files rather than for a whole key.
 */
export const equityErrorKey = (
  source: EquityErrorSource,
  error: EquityErrorType,
): string => `${source}.errors.${errorTypeSegment(error)}`
